import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PlusCircle, AlertCircle, Trash2, Calendar, SlidersHorizontal, InfoIcon, DollarSign } from 'lucide-react';
import { format, isValid, parse, isBefore, isAfter, isWithinInterval, addDays, startOfDay, endOfDay } from 'date-fns';
import { useTranslation } from '@/i18n/useTranslation';
import { DailyHourOverride } from '@/components/assignments/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toPlainDateString } from '@/utils/dateTransforms';

interface HourOverridesTableProps {
  overrides: DailyHourOverride[];
  onAdd: () => void;
  onUpdate: (id: number, field: keyof DailyHourOverride | string, value: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  assignmentStartDate?: Date;
  assignmentEndDate?: Date;
}

export function HourOverridesTable({
  overrides,
  onAdd,
  onUpdate,
  onDelete,
  assignmentStartDate,
  assignmentEndDate
}: HourOverridesTableProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // Check if a date is within any existing override
  const isDateOverlapping = (date: Date, currentOverrideId?: number): boolean => {
    if (!date || !isValid(date)) return false;
    
    // If there are no overrides, there can't be an overlap
    if (overrides.length === 0) return false;
    
    // Convert date to start of day for comparison
    const targetDate = startOfDay(date);
    
    return overrides.some(override => {
      // Skip the current override being edited
      if (override.id === currentOverrideId) return false;
      
      const startDate = override.start_date;
      const endDate = override.end_date;
      
      if (!startDate || !endDate || !isValid(startDate) || !isValid(endDate)) return false;
      
      // Ensure the target date is within the assignment period
      if (isBefore(targetDate, startOfDay(assignmentStartDate!)) || isAfter(targetDate, endOfDay(assignmentEndDate!))) {
        return false;
      }

      // Allow overrides that start and end on the same day
      return isWithinInterval(targetDate, { start: startOfDay(startDate), end: endOfDay(endDate) });
    });
  };
  
  // Check if there is at least one day in the assignment range without overrides
  const findFirstAvailableDate = (): Date | null => {
    if (!assignmentStartDate || !assignmentEndDate) {
      return null;
    }

    // Start from today or assignment start date (whichever is later)
    const today = startOfDay(new Date());
    let potentialDate = isBefore(assignmentStartDate, today) ? today : startOfDay(assignmentStartDate);

    // Iterate through the assignment period to find the first available date
    while (isBefore(potentialDate, addDays(assignmentEndDate, 1))) {
      if (!isDateOverlapping(potentialDate)) {
        return potentialDate;
      }
      potentialDate = addDays(potentialDate, 1);
    }

    // No available date found
    return null;
  };

  // Sort overrides by start date
  const sortedOverrides = [...overrides].sort((a, b) => {
    const dateA = a.start_date;
    const dateB = b.start_date;
    return dateA.getTime() - dateB.getTime();
  });

  // Check if an override overlaps with others
  const isOverrideOverlapping = (override: DailyHourOverride & { id: number }): boolean => {
    const startDate = override.start_date;
    const endDate = override.end_date;
  
    if (!startDate || !endDate || !isValid(startDate) || !isValid(endDate)) return false;
  
    return overrides.some((other) => {
      if (other.id === override.id) return false;
  
      const otherStartDate = other.start_date;
      const otherEndDate = other.end_date;
  
      if (!otherStartDate || !otherEndDate || !isValid(otherStartDate) || !isValid(otherEndDate)) return false;
  
      // Allow overrides that start and end on the same day
      return isWithinInterval(startDate, { start: startOfDay(otherStartDate), end: endOfDay(otherEndDate) }) ||
             isWithinInterval(endDate, { start: startOfDay(otherStartDate), end: endOfDay(otherEndDate) });
    });
  };

  const handleAddOverride = () => {
    onAdd();
  };

  // Prevent updating the assignment if overlaps exist
  const hasOverlaps = overrides.some((override) => isOverrideOverlapping(override));

  const handleDateSelected = async (id: number, field: string, date: Date) => {
    if (!isValid(date)) return;
    
    const isOverlapping = isDateOverlapping(date, id);
    if (isOverlapping) {
      toast({
        title: t('Date unavailable'),
        description: t('This date overlaps with an existing override period. Please select a different date.'),
        variant: "destructive",
      });
      return;
    }
    
    // Convert the Date to a YYYY-MM-DD string
    const formattedDate = format(date, 'yyyy-MM-dd');
    console.log(`Date selected for override ${id}, field ${field}, date ${formattedDate}`);
    
    await onUpdate(id, field, formattedDate);

    // If the start_date is higher than the end_date, update the end_date to match the new start_date
    if (field === 'start_date') {
      const override = overrides.find((o) => o.id === id);
      if (override?.end_date) {
        const endDate = override.end_date;
        if (isValid(endDate) && isBefore(endDate, date)) {
          const newEndDate = format(date, 'yyyy-MM-dd');
          console.log(`Adjusting end_date for override ${id} to match new start_date: ${newEndDate}`);
          await onUpdate(id, 'end_date', newEndDate);
        }
      }
    }

    // If the end_date is lower than the start_date, update the start_date to match the new end_date
    if (field === 'end_date') {
      const override = overrides.find((o) => o.id === id);
      if (override?.start_date) {
        const startDate = override.start_date;
        if (isValid(startDate) && isAfter(startDate, date)) {
          const newStartDate = format(date, 'yyyy-MM-dd');
          console.log(`Adjusting start_date for override ${id} to match new end_date: ${newStartDate}`);
          await onUpdate(id, 'start_date', newStartDate);
        }
      }
    }
  };

  const handleSliderChange = async (id: number, value: number[]) => {
    await onUpdate(id, 'daily_hours', value[0].toString());
  };
  
  const handleDeleteOverride = async (id: number) => {
    console.log(`Deleting override with ID: ${id} from HourOverridesTable`);
    try {
      await onDelete(id);
    } catch (error) {
      console.error(`Error deleting override ${id}:`, error);
    }
  };

  const handleUpdateOverride = async (id: number, field: string, value: string) => {
    await onUpdate(id, field, value); // Remove validation here
  };

  const handleToggleBillable = async (id: number, isCurrentlyBillable: boolean) => {
    await onUpdate(id, 'isBillable', (!isCurrentlyBillable).toString());
  };

  const renderDateCell = (override: DailyHourOverride & { id: number }, field: 'start_date' | 'end_date') => {
    const value = override[field];
    const parsedDate = value;
    
    // Function to disable dates outside assignment range or overlapping other overrides
    const disableDatePredicate = (date: Date) => {
      if (!assignmentStartDate || !assignmentEndDate) return false;

      // Normalize dates to remove timestamps
      const normalizedDate = new Date(date.setHours(0, 0, 0, 0));
      const normalizedStartDate = new Date(assignmentStartDate.setHours(0, 0, 0, 0));
      const normalizedEndDate = new Date(assignmentEndDate.setHours(0, 0, 0, 0));

      // Check if date is outside assignment range
      const outsideRange = isBefore(normalizedDate, normalizedStartDate) || 
                           isAfter(normalizedDate, normalizedEndDate);
                           
      // Check if date overlaps with other overrides (but not this one)
      const overlapping = isDateOverlapping(normalizedDate, override.id);
      
      return outsideRange || overlapping;
    };
    
        return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="flex gap-2 w-full justify-start">
            <Calendar className="h-4 w-4" />
            {toPlainDateString(value)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="single"
            selected={parsedDate}
            onSelect={(date) => date && handleDateSelected(override.id, field, date)}
            disabled={disableDatePredicate}
            initialFocus
            className="p-3 pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
      );
  };

  const renderDailyHoursCell = (override: DailyHourOverride & { id: number }) => {
    const dailyHours = override.daily_hours || 0;
    
    return (
      <div className="flex items-center gap-2 px-2">
        <Slider
          value={[dailyHours]}
          min={0}
          max={16}
          step={1}
          className="w-20"
          onValueChange={(value) => handleSliderChange(override.id, value)}
        />
        <span className="text-sm w-8 text-right">{dailyHours}</span>
      </div>
    );
  };

  const renderRowIndicator = (override: DailyHourOverride & { id: number }) => {
    if (override.isNew) { // Ensure isNew is properly checked
      return (
        <span className="text-xs text-green-600 flex items-center gap-1">
          <PlusCircle className="h-4 w-4" />
          {t('New')}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">{t('Daily Hour Overrides')}</h3>
        <Button
          onClick={handleAddOverride}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <PlusCircle className="h-4 w-4" />
          {t('Add Override')}
        </Button>
      </div>
      
      {overrides.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-4 border rounded-md bg-muted/20 text-muted-foreground">
          <AlertCircle className="h-10 w-10 mb-2 opacity-50" />
          <p>{t('No overrides set')}</p>
          <p className="text-sm">{t('Click Add Override to set custom daily hours for a specific period')}</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Start Date')}</TableHead>
              <TableHead>{t('End Date')}</TableHead>
              <TableHead>{t('Daily Hours')}</TableHead>
              <TableHead>{t('Description')}</TableHead>
              <TableHead>{t('Billable')}</TableHead> {/* New column */}
              <TableHead className="text-right">{t('Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedOverrides.map((override) => {
              const overlapping = isOverrideOverlapping(override);
          
              return (
                <TableRow key={override.id} className={overlapping ? 'bg-red-100' : ''}>
                  <TableCell>
                    {renderDateCell(override, 'start_date')}
                  </TableCell>
                  <TableCell>
                    {renderDateCell(override, 'end_date')}
                  </TableCell>
                  <TableCell>
                    {renderDailyHoursCell(override)}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      value={override.description || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        handleUpdateOverride(override.id, 'description', newValue);
                      }}
                      placeholder={t('Enter description')}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch 
                      checked={override.isBillable !== false} // Default to true if undefined
                      onCheckedChange={(checked) => {
                        handleToggleBillable(override.id, override.isBillable !== false);
                      }}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2 items-center">
                      {renderRowIndicator(override)}
                      {overlapping && (
                        <span className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          {t('Overlapping')}
                        </span>
                      )}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDeleteOverride(override.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {hasOverlaps && (
        <div className="text-sm text-red-600 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {t('There are overlapping overrides. Please resolve them before updating the assignment.')}
        </div>
      )}
    </div>
  );
}
